import { Injectable, OnInit } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { BehaviorSubject, catchError, combineLatest, concatMap, finalize, from, mergeMap, of, switchMap, tap } from 'rxjs';
import { BonusProductInfo, CartProductModel } from '../models/checkout';
import { ShopItemModel } from '../models/shop-item.model';
import { PerfilService } from './perfil.service';
import { CheckoutService } from './checkout.service';
import { ProfileDataRequest } from '../models/perfil';
import { EncryptionService } from './encryption.service';
import { StorageKeys } from '../models/storageKeys';

@Injectable({
  providedIn: 'root',
})

export class CartService {
  private sideMenuSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  sideMenu$: Observable<boolean> = this.sideMenuSubject.asObservable();

  private shopCart = new BehaviorSubject<CartProductModel[]>([]);
  shopCart$: Observable<CartProductModel[]> = this.shopCart.asObservable();

  private sessionCart = new BehaviorSubject<CartProductModel[]>([]);
  sessionCart$ = this.sessionCart.asObservable();

  public userTotalPoints: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  userTotalPoints$ = this.userTotalPoints.asObservable();

  public cartLoaded = false;

  public pointsLoaded = false;

  private userCartPoints: number = 0;

  private loggedUser!: ProfileDataRequest;

  constructor(
    private perfilService: PerfilService,
    private checkoutService: CheckoutService,
    private encryptSvc: EncryptionService) 
  {
    // Load sessionCart on service start
    const savedCart = sessionStorage.getItem('shopCart');
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        this.sessionCart.next(Array.isArray(parsed) ? parsed : []);
      } catch (err) {
        console.error('Invalid session cart', err);
        this.sessionCart.next([]);
      }
    }
  }

  public getShopCart(): BehaviorSubject<CartProductModel[]> {
    return this.shopCart;
  }

  loadCart() {
    this.loggedUser = this.perfilService.getCurrentUserInfo();
    this.checkoutService.getUserCart(this.loggedUser.userId, this.loggedUser.clientId)
      .pipe(
        concatMap(response => {
          this.updateShopCart(response);
          this.userCartPoints = 0;
          response.forEach(x => {
            if (x.usePoints === true) {
              this.userCartPoints += x.points * x.quantity;
            }
          });
          return this.checkoutService.getClientPoints(this.loggedUser.userId, this.loggedUser.clientId);
        }),
        finalize(() => {
        })
      )
      .subscribe(response => {
        this.userTotalPoints.next(response - this.userCartPoints);
      });
  }

  getCart() {
    return this.shopCart.asObservable();
  }

  toggleSideMenu() {
    this.sideMenuSubject.next(!this.sideMenuSubject.value);
  }

  updateShopCart(newCart: CartProductModel[]) {
    this.shopCart.next(newCart);
  }

  updateUserPoints(operation: string, points: number, oldQuantity?: number, newQuantity?: number) {
    let currentPoints = this.userTotalPoints.getValue();
    if (operation === 'add') {
      currentPoints -= points;
    }
    if (operation === 'remove') {
      currentPoints += points;
    }
    if (operation === 'update' && oldQuantity !== undefined && newQuantity !== undefined) {
      currentPoints += oldQuantity * points;
      currentPoints -= newQuantity * points;
    }

    this.userTotalPoints.next(currentPoints);
  }

  increaseCartMethod($event: ShopItemModel, usePoints: boolean, deferUpdate: boolean): Observable<boolean> {
    this.loggedUser = this.perfilService.getCurrentUserInfo();
    const currentShopCart = this.shopCart.getValue();
    const updatedCart = [...currentShopCart];
    let product = updatedCart.find(p => p.productId === $event.reference && p.usePoints === usePoints && p.previouslyUsingPoints === usePoints);
    if (!deferUpdate) {
      if (!product) {
        product = {
          userId: this.loggedUser.userId,
          clientId: this.loggedUser.clientId,
          productId: $event.reference,
          quantity: 1,
          previouslyUsingPoints: usePoints,
          usePoints: usePoints,
          points: $event.points,
          labFlashDiscount:$event.labFlashDiscount,
          labFlashBonus:$event.labFlashBonus
        };
        updatedCart.push(product);
      } else {
        product.quantity++;
      }
      if (usePoints) {
        this.updateUserPoints('add', $event.points);
      }
      this.shopCart.next(updatedCart);
    }
    if (product && deferUpdate) {
      return this.updateBackendCart(product, false).pipe(
        tap(opResult => {
          if (opResult) {
            this.shopCart.next(updatedCart);
          }
        }),
        catchError(err => {
          console.error('Erro ao atualizar backend (increase):', err);
          return of(false);
        })
      );
    }
    return of(false);
  }

  decreaseCartMethod($event: ShopItemModel, usePoints: boolean, deferUpdate: boolean): Observable<boolean> {
    this.loggedUser = this.perfilService.getCurrentUserInfo();
    const currentShopCart = this.shopCart.getValue();
    const updatedCart = [...currentShopCart];
    const productIndex = updatedCart.findIndex(p => p.productId === $event.reference && p.usePoints === usePoints && p.previouslyUsingPoints === usePoints);
    if (productIndex !== -1) {
      const product = updatedCart[productIndex];
      if (!deferUpdate) {
        if (product.quantity > 0) {
          product.quantity--;
        }
        this.shopCart.next(updatedCart);
        if (usePoints) {
          this.updateUserPoints('remove', $event.points);
        }
      }
      if (deferUpdate) {
        return this.updateBackendCart(product, false).pipe(
          tap((opResult) => {
            if (opResult) {
              this.shopCart.next(updatedCart);
            }
          }),
          catchError(err => {
            console.error('Erro ao atualizar backend (decrease):', err);
            return of(false);
          })
        );
      }
    }
    return of(false);
  }

  changeQuantityCartMethod($event: ShopItemModel, usePoints: boolean, input: number): Observable<boolean> {
    this.loggedUser = this.perfilService.getCurrentUserInfo();
    const currentShopCart = this.shopCart.getValue();
    let product = currentShopCart.find(p => p.productId === $event.reference && p.usePoints === usePoints);
    let oldQuantityForPoints = 0;


    if(this.loggedUser === undefined) 
      this.loggedUser = this.perfilService.getCurrentUserInfo();

    if (!product) {
      product = {
        userId: this.loggedUser.userId,
        clientId: this.loggedUser.clientId,
        productId: $event.reference,
        quantity: input,
        previouslyUsingPoints: usePoints,
        usePoints: usePoints,
        points: $event.points,
        labFlashDiscount:$event.labFlashDiscount,
        labFlashBonus:$event.labFlashBonus
      };
      currentShopCart.push(product);
    } else {
      if (input == 0) {
        const existingProductIndex = currentShopCart.findIndex(p => p.productId === $event.reference && p.usePoints === $event.isPoints);
        currentShopCart.splice(existingProductIndex, 1);
      }
      oldQuantityForPoints = product.quantity;
      product.quantity = input;
    }

    this.shopCart.next(currentShopCart);
    if (usePoints === true) {
      this.updateUserPoints('update', $event.points, oldQuantityForPoints, input);
    }

    return this.updateBackendCart(product, false).pipe(
      switchMap(opResult => {
        return of(opResult);
      })
    );
  }

  updateProduct(product: CartProductModel): Observable<boolean> {
    const currentShopCart = this.shopCart.getValue();
    const existingProductIndex = currentShopCart.findIndex(p => p.productId === product.productId && p.usePoints === product.usePoints);

    if (existingProductIndex !== -1) {
      if (product.quantity === 0) {
        currentShopCart.splice(existingProductIndex, 1);
      } else {
        currentShopCart[existingProductIndex].quantity = product.quantity;
      }
    } else if (product.quantity > 0) {
      currentShopCart.push(product);
    }

    this.shopCart.next(currentShopCart);

    return this.updateBackendCart(product, false).pipe(
      switchMap(opResult => {
        return of(opResult);
      })
    );
  }

  productPointsSwitcher(product: CartProductModel): Observable<boolean> {
    const currentShopCart = this.shopCart.getValue();

    let productPresent = currentShopCart.findIndex(p => p.productId === product.productId && p.usePoints === !(product.usePoints));
    let productToIncrement = currentShopCart.findIndex(p => p.productId === product.productId && p.usePoints === (product.usePoints));

    currentShopCart[productPresent].usePoints = product.usePoints;
    currentShopCart[productPresent].previouslyUsingPoints = product.usePoints;

    if (productToIncrement !== -1) {
      currentShopCart[productPresent].quantity += currentShopCart[productToIncrement].quantity;
      currentShopCart.splice(productToIncrement, 1);
    }

    this.shopCart.next(currentShopCart);

    return this.updateBackendCart(product, false).pipe(
      switchMap(opResult => {
        return of(opResult);
      })
    );
  }

  updateBackendCart(product: CartProductModel, isBonus: boolean): Observable<boolean> {
    return from(this.checkoutService.CrudCartProduct(product)).pipe(
      mergeMap(response => {
        return new Observable<boolean>(observer => {
          let opResult: boolean = response;
          observer.next(opResult);
          observer.complete();
        });
      }),
      catchError(error => {
        return new Observable<boolean>(observer => observer.next(false));
      })
    );
  }


  increaseDiscountQuantityCartMethod($event: ShopItemModel) {
    this.loggedUser = this.perfilService.getCurrentUserInfo();

    const savedCart = sessionStorage.getItem('shopCart');
    let parsedCart: CartProductModel[] = [];

    if (savedCart) {
      const temp = JSON.parse(savedCart);
      parsedCart = Array.isArray(temp) ? temp : [temp];
    }

    let sessionCartProduct = parsedCart.find(p => p.productId === $event.reference);

    if (!sessionCartProduct) {
      sessionCartProduct = {
        userId: this.loggedUser.userId,
        clientId: this.loggedUser.clientId,
        productId: $event.reference,
        quantity: $event.productQuantity,
        previouslyUsingPoints: false,
        usePoints: false,
        points: $event.points,
        labFlashDiscount: 1,
        labFlashBonus: $event.labFlashBonus
      };
      parsedCart.push(sessionCartProduct);
    } else {
      sessionCartProduct.labFlashDiscount = (sessionCartProduct.labFlashDiscount ?? 0) + 1;
    }

    this.updateCartSessionStorage(parsedCart);
  }


  changeDiscountQuantityCartMethod($event: ShopItemModel, labFlashDiscount: number) {
    this.loggedUser = this.perfilService.getCurrentUserInfo();

    const savedCart = sessionStorage.getItem('shopCart');
    let parsedCart: CartProductModel[] = [];

    if (savedCart) {
      const temp = JSON.parse(savedCart);
      parsedCart = Array.isArray(temp) ? temp : [temp];
    }

    let sessionCartProduct = parsedCart.find(p => p.productId === $event.reference);

    if (!sessionCartProduct) {
      sessionCartProduct = {
        userId: this.loggedUser.userId,
        clientId: this.loggedUser.clientId,
        productId: $event.reference,
        quantity: $event.productQuantity,
        previouslyUsingPoints: false,
        usePoints: false,
        points: $event.points,
        labFlashDiscount: labFlashDiscount,
        labFlashBonus: $event.labFlashBonus
      };
      parsedCart.push(sessionCartProduct);
    } else {
      if (labFlashDiscount === 0) {
        const index = parsedCart.findIndex(p => p.productId === $event.reference);
        if (index !== -1)
          parsedCart.splice(index, 1);
      } else {
        sessionCartProduct.labFlashDiscount = labFlashDiscount;
      }
    }

    this.updateCartSessionStorage(parsedCart);
  }

  decreaseDiscountQuantityCartMethod($event: ShopItemModel) {
    this.loggedUser = this.perfilService.getCurrentUserInfo();

    const savedCart = sessionStorage.getItem('shopCart');
    let parsedCart: CartProductModel[] = [];

    if (savedCart) {
      const temp = JSON.parse(savedCart);
      parsedCart = Array.isArray(temp) ? temp : [temp];
    }

    const index = parsedCart.findIndex(p => p.productId === $event.reference);
    if (index !== -1) {
      const product = parsedCart[index];
      if (product.labFlashDiscount && product.labFlashDiscount > 0) {
        product.labFlashDiscount--;
        if (product.labFlashDiscount === 0)
          parsedCart.splice(index, 1);
      }
    }

    this.updateCartSessionStorage(parsedCart);
  }


  increaseBonusQuantityCartMethod($event: ShopItemModel) {
    this.loggedUser = this.perfilService.getCurrentUserInfo();

    const savedCart = sessionStorage.getItem('shopCart');
    let parsedCart: CartProductModel[] = [];

    if (savedCart) {
      const temp = JSON.parse(savedCart);
      parsedCart = Array.isArray(temp) ? temp : [temp];
    }

    let sessionCartProduct = parsedCart.find(p => p.productId === $event.reference);

    // Convert ShopItemModel.bonus to BonusProductInfo[]
    const mappedBonus: BonusProductInfo[] = $event.bonus?.map(b => {
      const [required, given] = b.bonus.split("+").map(Number);

      return {
        bonusProductReference: b.referenceBonus,
        bonusProductName: b.description,
        bonusAccumulated: 0,
        bonusTiers: [
          {
            requiredProductQuantity: required || 0,
            bonusProductQuantityGiven: given || 0
          }
        ],
        imageBonus: b.imageBonus
      };
    }) || [];

    if (!sessionCartProduct) {
      sessionCartProduct = {
        userId: this.loggedUser.userId,
        clientId: this.loggedUser.clientId,
        productId: $event.reference,
        quantity: $event.productQuantity,
        previouslyUsingPoints: false,
        usePoints: false,
        points: $event.points,
        labFlashBonus: 1,
        labFlashDiscount: $event.labFlashDiscount,
        memoria:$event.decription,
        valor:$event.currentPrice,
        bonusProductInfo: mappedBonus,
        quantityCampaign: $event.quantityCampaign,
        discountPercentage: $event.discountPercentage,
        labDiscount: $event.labDiscount,
        otherDiscount: $event.otherDiscount,
      };
      parsedCart.push(sessionCartProduct);
    } else {
      sessionCartProduct.labFlashBonus = (sessionCartProduct.labFlashBonus ?? 0) + 1;
    }

    this.updateCartSessionStorage(parsedCart);
  }

  changeBonusQuantityCartMethod($event: ShopItemModel, labFlashBonus: number) {
    this.loggedUser = this.perfilService.getCurrentUserInfo();

    const savedCart = sessionStorage.getItem('shopCart');
    let parsedCart: CartProductModel[] = [];

    if (savedCart) {
      const temp = JSON.parse(savedCart);
      parsedCart = Array.isArray(temp) ? temp : [temp];
    }

    let sessionCartProduct = parsedCart.find(p => p.productId === $event.reference);

    // Convert ShopItemModel.bonus to BonusProductInfo[]
    const mappedBonus: BonusProductInfo[] = $event.bonus?.map(b => {
      const [required, given] = b.bonus.split("+").map(Number);

      return {
        bonusProductReference: b.referenceBonus,
        bonusProductName: b.description,
        bonusAccumulated: 0,
        bonusTiers: [
          {
            requiredProductQuantity: required || 0,
            bonusProductQuantityGiven: given || 0
          }
        ],
        imageBonus: b.imageBonus
      };
    }) || [];

    if (!sessionCartProduct) {
      sessionCartProduct = {
        userId: this.loggedUser.userId,
        clientId: this.loggedUser.clientId,
        productId: $event.reference,
        quantity: $event.productQuantity,
        previouslyUsingPoints: false,
        usePoints: false,
        points: $event.points,
        labFlashBonus: labFlashBonus,
        labFlashDiscount: $event.labFlashDiscount,
        memoria:$event.decription,
        valor:$event.currentPrice,
        bonusProductInfo: mappedBonus,
        quantityCampaign: $event.quantityCampaign,
        discountPercentage: $event.discountPercentage,
        labDiscount: $event.labDiscount,
        otherDiscount: $event.otherDiscount,
      };
      parsedCart.push(sessionCartProduct);
    } else {
      if (labFlashBonus === 0) {
        const index = parsedCart.findIndex(p => p.productId === $event.reference);
        if (index !== -1)
          parsedCart.splice(index, 1);
      } else {
        sessionCartProduct.labFlashBonus = labFlashBonus;
      }
    }

    this.updateCartSessionStorage(parsedCart);
  }

  decreaseBonusQuantityCartMethod($event: ShopItemModel) {
    this.loggedUser = this.perfilService.getCurrentUserInfo();

    const savedCart = sessionStorage.getItem('shopCart');
    let parsedCart: CartProductModel[] = [];

    if (savedCart) {
      const temp = JSON.parse(savedCart);
      parsedCart = Array.isArray(temp) ? temp : [temp];
    }

    const index = parsedCart.findIndex(p => p.productId === $event.reference);
    if (index !== -1) {
      const product = parsedCart[index];
      if (product.labFlashBonus && product.labFlashBonus > 0) {
        product.labFlashBonus--;
        if (product.labFlashBonus === 0)
          parsedCart.splice(index, 1);
      }
    }

    this.updateCartSessionStorage(parsedCart);
  }

  updateCartSessionStorage(cart: CartProductModel[]): void {
    sessionStorage.setItem('shopCart', JSON.stringify(cart));
    this.sessionCart.next(cart);
  }

  getSessionCart(): CartProductModel[] {
    return this.sessionCart.getValue();
  }

  clearSessionCart(): void {
    this.sessionCart.next([]);
  }

}